import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { tcpaZoocasaConsent } from 'domain/consent';
import { SiteLanguage } from 'types/locales';
import ZoocasaTheme from '@tenants/definitions/zoocasa/theme-config';
import { type HomeConfig, type TenantConfigHome, ZOOCASA_TENANT_ID, ZOOCASA_THEME_NAME, AllowedExperimentKey } from '@tenants/types';
import { createCurrencyMap } from '@tenants/utils/currency-map';

import type { ContentHosts, FooterConfig, TenantConfigFooter, HeadTagsConfig, TenantConfigHeadTags, ThemeConfig, ZOOCASA_TENANT_ID_TYPE, OrganizationConfig, TenantConfigOrganization, TenantConfigSEO, SEOConfig, AddressPageConfig, TenantConfigAddressPage, TenantConfigObservability, TenantConfigFilters } from '@tenants/types';
import type { Tenant } from '@tenants/types';
import { createFooterConfig } from './footer-config';
import { createOrganizationConfig } from './organization-config';
import { createAddressPageConfig } from './address-page-config';

import { Namespaces } from 'types/locales';
import { createHomeConfig } from '@tenants/definitions/zoocasa/home-config';
import { createLanguageConfig } from '@tenants/utils/configs';
import { canShowSchoolsCanadian } from '../shared/features';
import { ADDRESS_ROUTE } from 'components/dynamic-page/route-matchers';
import { CRITICAL_CITIES_CA } from '@tenants/constants/critical-cities';
import { createHeadTagsConfig } from './head-tags-config';

//#region Public Constants

export const ZOOCASA_L18N_NAMESPACES = [
  Namespaces.COMMON,
  Namespaces.ZOOCASA,
  Namespaces.SEARCH,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
  Namespaces.HOME,
  Namespaces.LISTING,
  Namespaces.HOME_APPRAISAL,
  Namespaces.CONNECTION_REQUEST,
  Namespaces.CONNECT_WITH_AGENT,
  Namespaces.INTERNAL_LINKS,
] as const;

//#endregion

//#region Public Types
export type ZoocasaTenantConfig = TenantConfigHome & TenantConfigFooter & TenantConfigOrganization & TenantConfigSEO & TenantConfigHeadTags & TenantConfigObservability & TenantConfigAddressPage & TenantConfigFilters;
export type ZoocasaTenant = Tenant<ZOOCASA_TENANT_ID_TYPE, ThemeConfig<typeof ZOOCASA_THEME_NAME>, ZoocasaTenantConfig>;
//#endregion

//#region Config Factories
// Use default CA config for build time, will be overridden dynamically on client side
const home: HomeConfig = createHomeConfig(CountryCodeList.CANADA);
const footer: FooterConfig = createFooterConfig();
const organization: OrganizationConfig = createOrganizationConfig();
const headTags: HeadTagsConfig = createHeadTagsConfig();
const addressPage: AddressPageConfig = createAddressPageConfig();
const seo: SEOConfig = {
  noIndexRoutes: [ADDRESS_ROUTE],
  noFollowRoutes: [ADDRESS_ROUTE],
};

// Supported countries for this tenant
const countries = [CountryCodeList.CANADA, CountryCodeList.UNITED_STATES];
//#endregion

//#region Tenant Definition
export const zoocasaTenant: ZoocasaTenant = {
  id: ZOOCASA_TENANT_ID,
  hosts: configJSON.hosts[ZOOCASA_TENANT_ID],
  contentHosts: configJSON.contentPageApi[ZOOCASA_TENANT_ID] as ContentHosts,
  blogApiUrl: configJSON.blogApi[ZOOCASA_TENANT_ID],
  theme: ZoocasaTheme,
  localization: {
    ...createLanguageConfig({
      globalLanguages: [SiteLanguage.English, SiteLanguage.French],
      standardLanguages: [SiteLanguage.English, SiteLanguage.French],
      globalDefaultLanguage: SiteLanguage.English,
      standardDefaultLanguage: SiteLanguage.English,
      namespaces: ZOOCASA_L18N_NAMESPACES,
      allowGlobalLocaleSwitching: false,
    }),
    countries,
    defaultCountry: CountryCodeList.CANADA,
    currencyMap: createCurrencyMap(countries),
  },
  config: {
    home,
    footer,
    organization,
    headTags,
    seo,
    addressPage,
    filters: {
      showComingSoonFilter: true,
    },
    observability: {
      enabled: true,
      areaPages: {
        enabled: true,
        criticalCities: CRITICAL_CITIES_CA,
      },
    },
  },
  routes: {
    claimed: '*',
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${ZOOCASA_TENANT_ID}"`,
    forceTenantFeatureValue: ZOOCASA_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: false,
    AccessibilityWidget: true,
    TrustArc: {
      enabled: true,
      domain: 'zoocasa.com',
    },
    ShowHybridAreaMapPage: true,
    /**
     * AreaPageV2 shell redesign. Force-off — GrowthBook experiment owns rollout.
     * `x-zoocasa-features: useAreaPageV2=true` or GrowthBook experiment.
     */
    UseAreaPageV2: false,
    UseSmartSearch: false,
    AddressPage: {
      showSchools: canShowSchoolsCanadian,
    },
    ShowQuebecPopup: false,
    UseZoocasaLightHomepage: true,
    UseBuildingGroupedPins: false,
    PoiAmenitiesFilter: false,
    Experiments: {
      [AllowedExperimentKey.STICKY_HEADER_TABS]: {
        enabled: true,
        observability: false,
      },
      [AllowedExperimentKey.ZOOPRAISAL_MORTGAGE_CTA]: {
        enabled: true,
        observability: true,
      },
    },
  },
  googleTagManager: {
    containerIds: {
      defaultContainerId: configJSON.googleTagManager.containerId,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '',
  },
  sitemap: {
    urls: [
      'https://www.zoocasa.com/sitemaps/area-pages/sitemap-index.xml',
      'https://www.zoocasa.com/sitemaps/address-pages/sitemap.xml',
      'https://www.zoocasa.com/sitemaps/root-pages/sitemap-index.xml',
      'https://www.zoocasa.com/sitemaps/cms/sitemap-index.xml',
      'https://www.zoocasa.com/blog/sitemap_index.xml',
    ],
  },
  robots: {
    disallowDomains: [],
    disallowPaths: [],
  },
  pvr: {
    phoneNumber: '1-844-683-4663',
  },
  mobileAppBanner: {
    name: 'apple-itunes-app',
    appId: '301743811',
  },
  consent: tcpaZoocasaConsent({ exempt: [CountryCodeList.CANADA] }),
};//#endregion
